/** @jsxImportSource preact */
import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'
import { finaliseRefusalMessage, overrideRefusalMessage } from '@plt/asc/src/ops-actions'

type FormAction =
  | 'accept-asc'
  | 'locate-booking-id-for-pnr'
  | 'finalise-asc'
  | 'override-asc-revalidation'
  | 'bulk-finalise-asc'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface BookingDetails {
  value: number
  grossFareCost?: number
  cancelationPenalty?: number
  travellersTotal?: number
  infantsTotal?: string
  currency?: string
  payment?: {
    totalPaid: number
    discountAmount: number
  }
  origin?: string
  destination?: string
  departureDate?: string
  returnDate?: string
  departureALTime?: string
  returnALTime?: string
  pnr?: string
  airlineReference?: string
  eticketNumber?: string
  voluntaryChange?: {
    vcCustomerChangeFeeCredFdPaid?: number
    vcCustomerChangeFeeCredFdPaidDate?: string
    vcSupplierChangeFeePaid?: number
    vcSupplierChangeFeePaidDate?: string
    vcPltCreditUpdatedPdFields?: string
    vcSendUpdatedTripConfirmation?: string
    vcQcComplete?: string
  }
}

interface ASCItem {
  id?: string
  pnr: string
  entryDateTime: string
  bookingReference?: string
  status?: string
  workflowId?: string
}

interface BulkFinaliseRow {
  pnr: string
  ascId?: string
  workflowId?: string
  bookingId?: string
  action: 'would-finalise' | 'finalised' | 'skipped' | 'error'
  reason: string
}

export function ASC() {
  const [bookingId, setBookingId] = useState('')
  const [selectedAction, setSelectedAction] = useState<FormAction>('accept-asc')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  // ASC form fields - prefilled with common values
  const [vcSendUpdatedTripConfirmation, setVcSendUpdatedTripConfirmation] = useState('yes')
  const [vcPltCreditUpdatedFields, setVcPltCreditUpdatedFields] = useState('vc-yes-completed')
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [departDate, setDepartDate] = useState('')
  const [returnDate, setReturnDate] = useState('')
  const [departTime, setDepartTime] = useState('')
  const [returnTime, setReturnTime] = useState('')
  const [recordLocatorPnr, setRecordLocatorPnr] = useState('')
  const [airlineReferenceNo, setAirlineReferenceNo] = useState('')
  const [eTicketNo, setETicketNo] = useState('')
  const [note, setNote] = useState('')

  const [pnr, setPnr] = useState('')
  const [ascs, setAscs] = useState<ASCItem[]>([])
  const [selectedAscIndex, setSelectedAscIndex] = useState<number>(-1)
  const [fetchingAscs, setFetchingAscs] = useState(false)

  const [ascConfirmed, setAscConfirmed] = useState(false)
  const [overrideConfirmed, setOverrideConfirmed] = useState(false)
  const [decision, setDecision] = useState<'finalised' | 'aborted'>('finalised')

  const [bulkPnrInput, setBulkPnrInput] = useState('')
  const [bulkResults, setBulkResults] = useState<BulkFinaliseRow[]>([])
  const [bulkPhase, setBulkPhase] = useState<'idle' | 'previewing' | 'previewed' | 'finalising' | 'done'>('idle')
  const [bulkError, setBulkError] = useState('')
  // A second click lands before the disabled button re-renders; this drops it.
  const submitting = useRef(false)
  // Clears the success message; cancelled on reset and on the next submit so it can't end a later status.
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The change just finalised or overridden stays in the list with its old status until the reload
  // lands, so its row can't be picked again until then.
  const [actionedWorkflowId, setActionedWorkflowId] = useState<string | null>(null)

  // Set default note prefill
  useEffect(() => {
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setNote(notePrefill)
    }
  }, [user])

  useEffect(() => {
    if (frontContext && selectedAction !== 'locate-booking-id-for-pnr') {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext, selectedAction])

  useEffect(() => {
    if (selectedAction === 'locate-booking-id-for-pnr' && pnr.trim()) {
      const timeoutId = setTimeout(() => {
        fetchAscsByPNR(pnr.trim())
      }, 800)
      return () => clearTimeout(timeoutId)
    } else if (
      (selectedAction === 'finalise-asc' || selectedAction === 'override-asc-revalidation') &&
      bookingId.trim()
    ) {
      const timeoutId = setTimeout(() => {
        fetchAscsByBookingId(bookingId.trim())
      }, 800)
      return () => clearTimeout(timeoutId)
    } else {
      setAscs([])
      setSelectedAscIndex(-1)
    }
  }, [selectedAction, pnr, bookingId])

  const fetchAscsByPNR = async (pnrValue: string) => {
    if (!pnrValue) return

    setFetchingAscs(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/ascs/by-pnr/${pnrValue}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch ASCs')
      }

      const data: ASCItem[] = await response.json()
      setAscs(data)
    } catch (error) {
      console.error('Error fetching ASCs by PNR:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch ASCs')
      setAscs([])
    } finally {
      setFetchingAscs(false)
      setActionedWorkflowId(null)
    }
  }

  const fetchAscsByBookingId = async (id: string) => {
    if (!id) return

    setFetchingAscs(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}/ascs`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch ASCs')
      }

      const data: ASCItem[] = await response.json()
      setAscs(data)
    } catch (error) {
      console.error('Error fetching ASCs by booking ID:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch ASCs')
      setAscs([])
    } finally {
      setFetchingAscs(false)
      setActionedWorkflowId(null)
    }
  }

  const fetchBookingDetails = useCallback(async (id: string) => {
    if (!id.trim()) {
      return
    }

    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch booking details')
      }

      const details: BookingDetails = await response.json()

      // Auto-populate fields from booking details if available
      if (details.origin) {
        setOrigin(details.origin)
      }
      if (details.destination) {
        setDestination(details.destination)
      }
      if (details.departureDate) {
        setDepartDate(details.departureDate)
      }
      if (details.returnDate) {
        setReturnDate(details.returnDate)
      }
      if (details.departureALTime) {
        // Extract just the time portion if it's a full datetime
        const timePart = details.departureALTime.includes('T')
          ? details.departureALTime.split('T')[1].substring(0, 5)
          : details.departureALTime
        setDepartTime(timePart)
      }
      if (details.returnALTime) {
        // Extract just the time portion if it's a full datetime
        const timePart = details.returnALTime.includes('T')
          ? details.returnALTime.split('T')[1].substring(0, 5)
          : details.returnALTime
        setReturnTime(timePart)
      }
      if (details.pnr) {
        setRecordLocatorPnr(details.pnr)
      }
      if (details.airlineReference) {
        setAirlineReferenceNo(details.airlineReference)
      }
      if (details.eticketNumber) {
        setETicketNo(details.eticketNumber)
      }

      // Extract and set voluntary change fields if available
      if (details.voluntaryChange) {
        if (details.voluntaryChange.vcSendUpdatedTripConfirmation) {
          const tripConfirmation = details.voluntaryChange.vcSendUpdatedTripConfirmation.toLowerCase()
          setVcSendUpdatedTripConfirmation(tripConfirmation === 'yes' ? 'yes' : 'no')
        }

        if (details.voluntaryChange.vcPltCreditUpdatedPdFields) {
          // Map the text value to the corresponding dropdown value
          const fieldValue = details.voluntaryChange.vcPltCreditUpdatedPdFields
          if (fieldValue.includes('Not Required') || fieldValue.includes('Method 1')) {
            setVcPltCreditUpdatedFields('plt-credit-not-required-method-1')
          } else if (fieldValue.includes('Yes Completed') && fieldValue.includes('Method 2')) {
            setVcPltCreditUpdatedFields('plt-credit-yes-completed-method-2')
          } else if (fieldValue.includes('Yes Completed')) {
            setVcPltCreditUpdatedFields('vc-yes-completed')
          }
        }
      }
    } catch (error) {
      console.error('Error fetching booking details:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch booking details')
    }
  }, [])

  // Fetch booking details when ID is entered manually
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (bookingId.trim()) {
        fetchBookingDetails(bookingId.trim())
      }
    }, 800)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [bookingId, fetchBookingDetails])

  const clearSuccessTimer = () => {
    if (successTimer.current) clearTimeout(successTimer.current)
    successTimer.current = null
  }

  useEffect(() => clearSuccessTimer, [])

  const resetForm = () => {
    if (submitting.current) return

    clearSuccessTimer()
    setBookingId('')
    setFormStatus('idle')
    setErrorMessage('')
    setVcSendUpdatedTripConfirmation('yes')
    setVcPltCreditUpdatedFields('vc-yes-completed')
    setOrigin('')
    setDestination('')
    setDepartDate('')
    setReturnDate('')
    setDepartTime('')
    setReturnTime('')
    setRecordLocatorPnr('')
    setAirlineReferenceNo('')
    setETicketNo('')
    setPnr('')
    setAscs([])
    setSelectedAscIndex(-1)
    setAscConfirmed(false)
    setOverrideConfirmed(false)
    setDecision('finalised')

    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setNote(notePrefill)
    }
  }

  const handleRefreshAscs = () => {
    if (selectedAction === 'locate-booking-id-for-pnr' && pnr) {
      fetchAscsByPNR(pnr.trim())
    } else if (bookingId) {
      fetchAscsByBookingId(bookingId.trim())
    }
  }

  const handleAscSelectionChange = (index: number) => {
    setSelectedAscIndex(index)
    setAscConfirmed(false)
    setOverrideConfirmed(false)
    setDecision('finalised')
  }

  const handleActionChange = (e: Event) => {
    if (submitting.current) return

    const target = e.target as HTMLSelectElement
    const newAction = target.value as FormAction
    setSelectedAction(newAction)
    setErrorMessage('')
    setAscConfirmed(false)
    setOverrideConfirmed(false)
    setDecision('finalised')
    setBulkPnrInput('')
    setBulkResults([])
    setBulkPhase('idle')
    setBulkError('')
    if (newAction === 'locate-booking-id-for-pnr') {
      setBookingId('')
    }
  }

  // Loose on purpose — accepts one-per-line, comma-separated, or a pasted CSV column;
  // same 6-char-alnum-token convention as tmp/asc-investigator/pull-all.sh.
  const parseBulkPnrs = (raw: string): string[] => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const token of raw.split(/[\s,"]+/)) {
      const p = token.trim().toUpperCase()
      if (/^[A-Z0-9]{6}$/.test(p) && !seen.has(p)) {
        seen.add(p)
        out.push(p)
      }
    }
    return out
  }

  const runBulkFinalise = async (dryRun: boolean) => {
    const pnrs = parseBulkPnrs(bulkPnrInput)
    if (pnrs.length === 0) {
      setBulkError('Enter at least one PNR')
      return
    }

    setBulkError('')
    setBulkPhase(dryRun ? 'previewing' : 'finalising')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/asc/bulk-finalise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pnrs, dryRun }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Bulk finalise request failed')
      }

      const data: { results: BulkFinaliseRow[] } = await response.json()
      setBulkResults(data.results)
      setBulkPhase(dryRun ? 'previewed' : 'done')
    } catch (error) {
      console.error('Error running bulk finalise:', error)
      setBulkError(error instanceof Error ? error.message : 'Bulk finalise request failed')
      setBulkPhase('idle')
    }
  }

  const handleFormSubmit = async (e: Event) => {
    e.preventDefault()

    if (submitting.current) return

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    submitting.current = true
    clearSuccessTimer()
    setFormStatus('loading')
    setErrorMessage('')

    try {
      let endpoint: string = ''
      let requestBody: any = {}

      if (selectedAction === 'accept-asc') {
        endpoint = `${config.apiBaseUrl}/bookings/${bookingId.trim()}/accept-asc`
        requestBody = {
          'vc-send-updated-trip-confirmation': vcSendUpdatedTripConfirmation,
          'vc-plt-credit-updated-pd-fields': vcPltCreditUpdatedFields,
          origin,
          destination,
          'depart-date': departDate,
          'depart-al-time': departDate && departTime ? `${departTime}` : departTime,
          'record-locator-pnr': recordLocatorPnr,
          'airline-reference-no': airlineReferenceNo,
          'e-ticket-no': eTicketNo,
          note,
          ...(returnDate !== '' && returnDate !== undefined && returnDate !== null
            ? {
                'return-date': returnDate,
                'return-al-time': returnDate && returnTime ? `${returnTime}` : returnTime,
              }
            : {}),
        }
      } else if (selectedAction === 'locate-booking-id-for-pnr') {
        if (selectedAscIndex === -1 || !ascs[selectedAscIndex]) {
          setErrorMessage('Please select an ASC from the table')
          setFormStatus('error')
          return
        }

        const selectedAsc = ascs[selectedAscIndex]

        if (!selectedAsc.workflowId) {
          setErrorMessage('Selected ASC does not have a workflow ID')
          setFormStatus('error')
          return
        }

        endpoint = `${config.apiBaseUrl}/bookings/locate-booking-id-for-pnr`
        requestBody = {
          bookingId: bookingId.trim(),
          ascWorkflowId: selectedAsc.workflowId,
        }
      } else if (selectedAction === 'finalise-asc') {
        if (selectedAscIndex === -1 || !ascs[selectedAscIndex]) {
          setErrorMessage('Please select an ASC from the table')
          setFormStatus('error')
          return
        }

        const selectedAsc = ascs[selectedAscIndex]

        if (!selectedAsc.workflowId) {
          setErrorMessage('Selected ASC does not have a workflow ID')
          setFormStatus('error')
          return
        }

        const refusal = finaliseRefusalMessage(selectedAsc.status)
        if (refusal) {
          setErrorMessage(refusal)
          setFormStatus('error')
          return
        }

        endpoint = `${config.apiBaseUrl}/bookings/finalise-asc`
        requestBody = {
          ascWorkflowId: selectedAsc.workflowId,
          decision,
        }
      } else if (selectedAction === 'override-asc-revalidation') {
        if (selectedAscIndex === -1 || !ascs[selectedAscIndex]) {
          setErrorMessage('Please select an ASC from the table')
          setFormStatus('error')
          return
        }

        const selectedAsc = ascs[selectedAscIndex]

        if (!selectedAsc.id) {
          setErrorMessage('Selected ASC does not have an ID')
          setFormStatus('error')
          return
        }

        const refusal = overrideRefusalMessage(selectedAsc.status)
        if (refusal) {
          setErrorMessage(refusal)
          setFormStatus('error')
          return
        }

        endpoint = `${config.apiBaseUrl}/bookings/override-asc-revalidation`
        requestBody = {
          ascId: selectedAsc.id,
        }
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit form')
      }

      setFormStatus('success')
      successTimer.current = setTimeout(() => {
        successTimer.current = null
        setFormStatus('idle')
      }, 3000)

      // The workflow moves a finalised change on within seconds, so the reload shows its new
      // status before Ops pick the next action. Until then the list still shows the old status,
      // so the selection is cleared rather than left ready to finalise the same change again.
      if (selectedAction === 'finalise-asc' || selectedAction === 'override-asc-revalidation') {
        setActionedWorkflowId(ascs[selectedAscIndex]?.workflowId ?? null)
        setSelectedAscIndex(-1)
        setAscConfirmed(false)
        setOverrideConfirmed(false)
        setTimeout(handleRefreshAscs, 3000)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit form')
      setFormStatus('error')
    } finally {
      submitting.current = false
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">ASC Operations</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">ASC Operations</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      {selectedAction !== 'locate-booking-id-for-pnr' && selectedAction !== 'bulk-finalise-asc' && (
        <div className="mb-6">
          <label
            htmlFor="bookingId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking ID <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="bookingId"
              type="text"
              value={bookingId}
              onChange={e => setBookingId((e.target as HTMLInputElement).value)}
              disabled={formStatus === 'loading'}
              className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
              placeholder="Enter Booking ID"
              required
            />
          </div>
          {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
          {errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
          {bookingId && isFrontApp && (
            <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
          )}
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="action"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Select Action
        </label>
        <select
          id="action"
          value={selectedAction}
          onChange={handleActionChange}
          disabled={formStatus === 'loading'}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="accept-asc">Accept ASC</option>
          <option value="locate-booking-id-for-pnr">Locate Booking ID For PNR</option>
          <option value="finalise-asc">Finalise ASC</option>
          <option value="override-asc-revalidation">Override ASC Revalidation</option>
          <option value="bulk-finalise-asc">Bulk Finalise ASC</option>
        </select>
      </div>

      {selectedAction === 'locate-booking-id-for-pnr' && (
        <>
          <div className="mb-6">
            <label
              htmlFor="pnr"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              PNR <span className="text-red-500">*</span>
            </label>
            <input
              id="pnr"
              type="text"
              value={pnr}
              onChange={e => setPnr((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter PNR"
              required
            />
          </div>
        </>
      )}

      {(selectedAction === 'locate-booking-id-for-pnr' ||
        selectedAction === 'finalise-asc' ||
        selectedAction === 'override-asc-revalidation') && (
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-800 mr-2">ASCs</h2>
            <button
              type="button"
              onClick={handleRefreshAscs}
              className="text-indigo-600 hover:text-indigo-800 focus:outline-none"
              title="Refresh ASCs"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>

          {fetchingAscs ? (
            <div className="p-4 bg-gray-50 rounded-md flex justify-center items-center">
              <svg
                className="animate-spin h-6 w-6 text-indigo-600 mr-2"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Loading ASCs...</span>
            </div>
          ) : (
            <>
              {ascs.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-md text-center">
                  {selectedAction === 'locate-booking-id-for-pnr'
                    ? pnr
                      ? 'No ASCs found for this PNR.'
                      : 'Enter a PNR to view ASCs.'
                    : bookingId
                      ? 'No ASCs found for this booking.'
                      : 'Enter a booking ID to view ASCs.'}
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-md">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Select
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Workflow ID
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          PNR
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Entry Date Time
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Booking Reference
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ascs.map((asc, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="radio"
                              name="ascSelection"
                              value={index}
                              checked={selectedAscIndex === index}
                              disabled={!!actionedWorkflowId && asc.workflowId === actionedWorkflowId}
                              onChange={() => handleAscSelectionChange(index)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{asc.workflowId || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{asc.pnr}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{asc.status || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(asc.entryDateTime).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {asc.bookingReference || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <form
        onSubmit={handleFormSubmit}
        className="space-y-4"
      >
        {selectedAction === 'accept-asc' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Send Updated Trip Confirmation <span className="text-red-500">*</span>
                </label>
                <select
                  value={vcSendUpdatedTripConfirmation}
                  onChange={e => setVcSendUpdatedTripConfirmation((e.target as HTMLSelectElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PLT Credit Updated PD Fields <span className="text-red-500">*</span>
                </label>
                <select
                  value={vcPltCreditUpdatedFields}
                  onChange={e => setVcPltCreditUpdatedFields((e.target as HTMLSelectElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="vc-yes-completed">VC Yes Completed</option>
                  <option value="plt-credit-not-required-method-1">PLT Credit Not Required Method 1</option>
                  <option value="plt-credit-yes-completed-method-2">PLT Credit Yes Completed Method 2</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Origin <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={origin}
                  onChange={e => setOrigin((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Destination <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={destination}
                  onChange={e => setDestination((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={departDate}
                  onChange={e => setDepartDate((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Departure Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={departTime}
                  onChange={e => setDepartTime((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. 14:30"
                  pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                  title="Please enter time in HH:MM format (24-hour)"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                <input
                  type="date"
                  value={returnDate}
                  onChange={e => setReturnDate((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
                <input
                  type="text"
                  value={returnTime}
                  onChange={e => setReturnTime((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. 16:45"
                  pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                  title="Please enter time in HH:MM format (24-hour)"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Record Locator/PNR <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={recordLocatorPnr}
                  onChange={e => setRecordLocatorPnr((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Airline Reference No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={airlineReferenceNo}
                  onChange={e => setAirlineReferenceNo((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Ticket No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={eTicketNo}
                  onChange={e => setETicketNo((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={note}
                onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              />
            </div>
          </>
        )}

        {selectedAction === 'locate-booking-id-for-pnr' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              This action will locate the booking ID for the given PNR. Please select an ASC from the table above and
              provide the booking ID to resolve.
            </p>
            {selectedAscIndex !== -1 && ascs[selectedAscIndex] && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Selected ASC:</strong> PNR {ascs[selectedAscIndex].pnr} -{' '}
                  {new Date(ascs[selectedAscIndex].entryDateTime).toLocaleString()}
                </p>
              </div>
            )}
            <div>
              <label
                htmlFor="bookingId"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Booking ID <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="bookingId"
                  type="text"
                  value={bookingId}
                  onChange={e => setBookingId((e.target as HTMLInputElement).value)}
                  disabled={formStatus === 'loading'}
                  className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                  placeholder="Enter Booking ID"
                  required
                />
              </div>
              {formStatus === 'error' && !bookingId && (
                <p className="mt-1 text-sm text-red-600">Booking ID is required</p>
              )}
              {errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
              {isFrontApp && (
                <p className="mt-1 text-xs text-amber-600">
                  Note: Booking ID auto-detection is disabled for PNR lookup. Please enter manually.
                </p>
              )}
            </div>
          </div>
        )}

        {selectedAction === 'finalise-asc' && (
          <div className="space-y-4">
            {selectedAscIndex !== -1 && ascs[selectedAscIndex] && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Selected ASC:</strong> PNR {ascs[selectedAscIndex].pnr} -{' '}
                  {new Date(ascs[selectedAscIndex].entryDateTime).toLocaleString()}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Decision</label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-indigo-600"
                    name="decision"
                    value="finalised"
                    checked={decision === 'finalised'}
                    onChange={() => setDecision('finalised')}
                  />
                  <span className="ml-2">Finalised</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-indigo-600"
                    name="decision"
                    value="aborted"
                    checked={decision === 'aborted'}
                    onChange={() => setDecision('aborted')}
                  />
                  <span className="ml-2">Aborted</span>
                </label>
              </div>
            </div>

            <div className="flex items-start">
              <input
                id="ascConfirmation"
                type="checkbox"
                checked={ascConfirmed}
                onChange={e => setAscConfirmed((e.target as HTMLInputElement).checked)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="ascConfirmation"
                className="ml-2 text-sm text-gray-700"
              >
                {decision === 'finalised'
                  ? 'Confirming ASC is confirmed and revalidated'
                  : 'Confirming no follow up and no check asc clearance will happen'}
              </label>
            </div>
          </div>
        )}

        {selectedAction === 'override-asc-revalidation' && (
          <div className="space-y-4">
            {selectedAscIndex !== -1 && ascs[selectedAscIndex] && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Selected ASC:</strong> PNR {ascs[selectedAscIndex].pnr} -{' '}
                  {new Date(ascs[selectedAscIndex].entryDateTime).toLocaleString()}
                </p>
              </div>
            )}
            <div className="flex items-start">
              <input
                id="overrideConfirmation"
                type="checkbox"
                checked={overrideConfirmed}
                onChange={e => setOverrideConfirmed((e.target as HTMLInputElement).checked)}
                className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="overrideConfirmation"
                className="ml-2 text-sm text-gray-700"
              >
                Confirming this override is intended for bypassing ASC revalidation, which means the workflow won't
                check the consistency between itinerary and ticket
              </label>
            </div>
          </div>
        )}

        {selectedAction !== 'bulk-finalise-asc' && (
          <div className="pt-4">
            <button
              type="submit"
              disabled={
                formStatus === 'loading' ||
                formStatus === 'success' ||
                (selectedAction === 'finalise-asc' && !ascConfirmed) ||
                (selectedAction === 'override-asc-revalidation' && !overrideConfirmed)
              }
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading'
                ? 'Processing...'
                : selectedAction === 'accept-asc'
                  ? 'Accept ASC'
                  : selectedAction === 'locate-booking-id-for-pnr'
                    ? 'Locate Booking ID'
                    : selectedAction === 'finalise-asc'
                      ? decision === 'finalised'
                        ? 'Finalise ASC'
                        : 'Abort ASC'
                      : 'Override ASC Revalidation'}
            </button>
          </div>
        )}
      </form>

      {selectedAction === 'bulk-finalise-asc' && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="bulkPnrs"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              PNRs <span className="text-red-500">*</span>
            </label>
            <textarea
              id="bulkPnrs"
              rows={6}
              value={bulkPnrInput}
              onChange={e => setBulkPnrInput((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
              placeholder={'One PNR per line, or paste a CSV column — e.g.\nIVDYQF\nONGOOX'}
            />
            <p className="mt-1 text-xs text-gray-500">
              {parseBulkPnrs(bulkPnrInput).length} PNR(s) recognised. Only ASCs currently{' '}
              <code className="bg-gray-100 px-1 rounded">sent-to-manual-handling</code> are touched, and only finalised
              if Sabre already reports the schedule change resolved — everything else is left alone and listed below
              with why.
            </p>
          </div>

          {bulkError && <p className="text-sm text-red-600">{bulkError}</p>}

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => runBulkFinalise(true)}
              disabled={bulkPhase === 'previewing' || bulkPhase === 'finalising'}
              className="px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkPhase === 'previewing' ? 'Checking Sabre…' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={() => runBulkFinalise(false)}
              disabled={
                bulkPhase !== 'previewed' || bulkResults.filter(r => r.action === 'would-finalise').length === 0
              }
              title={bulkPhase !== 'previewed' ? 'Preview first' : undefined}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bulkPhase === 'finalising'
                ? 'Finalising…'
                : `Finalise ${bulkResults.filter(r => r.action === 'would-finalise').length} Resolved`}
            </button>
          </div>

          {bulkResults.length > 0 && (
            <div className="mt-2 border border-gray-200 rounded-md overflow-hidden overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">PNR</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">Result</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-700">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bulkResults.map((row, i) => (
                    <tr key={`${row.pnr}-${i}`}>
                      <td className="px-3 py-2 font-mono whitespace-nowrap">{row.pnr}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span
                          className={
                            'px-2 py-0.5 rounded-full text-xs font-medium ' +
                            (row.action === 'finalised'
                              ? 'bg-green-100 text-green-800'
                              : row.action === 'would-finalise'
                                ? 'bg-blue-100 text-blue-800'
                                : row.action === 'error'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800')
                          }
                        >
                          {row.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {formStatus === 'success' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">
            {selectedAction === 'accept-asc'
              ? 'ASC accepted successfully!'
              : selectedAction === 'locate-booking-id-for-pnr'
                ? 'Booking ID located successfully!'
                : 'ASC finalised successfully!'}
          </p>
        </div>
      )}

      {formStatus === 'error' && errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="mt-6">
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={resetForm}
            disabled={formStatus === 'loading'}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Form
          </button>
        </div>
      </div>
    </div>
  )
}
